import { useMemo } from "react";

/**
 * Filtra usuarios visibles según uno o varios dispositivos y el nivel jerárquico.
 * - Un usuario puede ver un dispositivo si todos los niveles que él tiene definidos coinciden con los del dispositivo.
 * - Si se pasan varios dispositivos, el usuario debe poder ver TODOS ellos (intersección).
 * - Los usuarios más arriba (menos niveles definidos) pueden ver todo lo que esté por debajo.
 * - Si el usuario logueado NO es master, no puede ver usuarios master.
 *
 * @param {object|Array|null} deviceInput - Dispositivo o lista de dispositivos seleccionados (cada uno con .data)
 * @param {Array} users - Lista total de usuarios
 * @param {string} privilegeUser - Privilegio del usuario actualmente logueado
 */

export default function useFilteredUsersByDevice(deviceInput, users, privilegeUser) {
  return useMemo(() => {
    if (!deviceInput || !users?.length) return [];

    // Normalizar a array (si viene un solo device)
    const devices = Array.isArray(deviceInput)
      ? deviceInput.map(d => d.data || d)
      : [deviceInput.data || deviceInput];

    // Función para determinar si un usuario puede ver UN dispositivo
    const canSeeDevice = (user, d) => {
      // Si no coincide empresa, ya no aplica
      if (user.id_company !== d.company_fk) return false;

      if (user.id_branch && user.id_branch !== d.branch_fk) return false;
      if (user.id_place && user.id_place !== d.place_fk) return false;
      if (user.id_zone && user.id_zone !== d.zone_fk) return false;
      if (user.id_local && user.id_local !== d.local_fk) return false;

      return true;
    };

    // Filtrar usuarios
    return users.filter((user) => {
      // Ocultar masters si el logueado no es master
      if (privilegeUser !== "master" && user.privilege === "master") return false;

      // Master (si permitido) puede ver todo
      if (user.privilege === "master") return true;

      // Debe poder ver tods los dispositivos seleccionados
      return devices.every((d) => canSeeDevice(user, d));
    });
  }, [deviceInput, users, privilegeUser]);
}
